import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OrgUserService from '../../../services/orgUser.service';
import { toast } from 'react-toastify';
import '../../../styles/dashboard/OrgUser/OrgUserHome.css';

export default function OrgUserHome() {
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchDashboardStats();
  }, []);
  
  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await OrgUserService.getDashboardStats();
      setStats(response.data);
      setUser({
        ...response.data.user,
        organizationName: response.data.user.organization?.organizationName || 'N/A',
        organizationId: response.data.user.organization?.id || '',
        memberCode: response.data.user.organization?.memberCode || 'N/A'
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error(error.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="orguser-home">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }
  
  if (!stats) {
    return (
      <div className="orguser-home">
        <div className="error">Failed to load dashboard. Please refresh the page.</div>
      </div>
    );
  }

  return (
    <div className="orguser-home">
      <div className="header-section">
        <h1>Welcome!</h1>
        <p>Your Organization Portal Dashboard</p>
        {user?.organizationName && (
          <div className="organization-info">
            <div className="organization-name">
              <span className="org-label">Organization:</span>
              <span className="org-value">{user.organizationName}</span>
            </div>
            <div className="organization-code">
              <span className="code-label">Member Code:</span>
              <span className="code-value">{user.memberCode}</span>
            </div>
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card credit-checks" onClick={() => navigate('/orguser/credit-checks')}>
          <div className="kpi-icon">📊</div>
          <div className="kpi-content">
            <h3>Credit Checks</h3>
            <p className="kpi-value">{stats.stats.totalCreditChecks}</p>
            <p className="kpi-label">Total Checks Performed</p>
          </div>
        </div>

        <div className="kpi-card credit-reports" onClick={() => navigate('/orguser/credit-reports')}>
          <div className="kpi-icon">📋</div>
          <div className="kpi-content">
            <h3>Credit Reports</h3>
            <p className="kpi-value">{stats.stats.totalCreditReports}</p>
            <p className="kpi-label">Organization Reports</p>
          </div>
        </div>

        <div className="kpi-card wallet" onClick={() => navigate('/orguser/wallet')}>
          <div className="kpi-icon"></div>
          <div className="kpi-content">
            <h3>Wallet Balance</h3>
            <p className="kpi-value">₹{Number(stats.stats.walletBalance || 0).toFixed(2)}</p>
            <p className="kpi-label">Available Balance</p>
          </div>
        </div>

        <div className="kpi-card tickets" onClick={() => navigate('/orguser/support-tickets')}>
          <div className="kpi-icon">🎫</div>
          <div className="kpi-content">
            <h3>Support Tickets</h3>
            <p className="kpi-value">{stats.stats.openTickets}</p>
            <p className="kpi-label">Open Tickets</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity-section">
        <div className="section-header">
          <h2>Recent Activity</h2>
          <a href="/orguser/activity-logs" className="view-all">View All →</a>
        </div>

        {stats.recentActivity && stats.recentActivity.length > 0 ? (
          <div className="recent-activity-list">
            {stats.recentActivity.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-icon">🔍</div>
                <div className="activity-details">
                  <h4>{activity.entityName}</h4>
                  <p>Credit Check - {activity.status}</p>
                  <span className="activity-date">
                    {new Date(activity.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <span className={`status-badge ${activity.status}`}>
                  {activity.status}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No recent activity</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <button
            className="action-btn"
            onClick={() => navigate('/orguser/credit-checks')}
          >
            <span className="action-icon">➕</span>
            <span>New Credit Check</span>
          </button>
          <button
            className="action-btn"
            onClick={() => navigate('/orguser/credit-reports')}
          >
            <span className="action-icon">📊</span>
            <span>View Reports</span>
          </button>
          <button
            className="action-btn"
            onClick={() => navigate('/orguser/wallet')}
          >
            <span className="action-icon">💳</span>
            <span>Manage Wallet</span>
          </button>
          <button
            className="action-btn"
            onClick={() => navigate('/orguser/support-tickets')}
          >
            <span className="action-icon">💬</span>
            <span>Support</span>
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="stats-overview">
        <div className="stat-box" onClick={() => navigate('/orguser/credit-checks')}>
          <h3>Organization Checks</h3>
          <p className="stat-value">{stats.stats.orgTotalChecks}</p>
          <p className="stat-label">Total checks in organization</p>
        </div>
        <div className="stat-box" onClick={() => navigate('/orguser/wallet')}>
          <h3>Wallet Top-ups</h3>
          <p className="stat-value">₹{Number(stats.stats.walletTotalTopup || 0).toFixed(2)}</p>
          <p className="stat-label">Total top-ups received</p>
        </div>
      </div>
    </div>
  );
}
